import React, { useState, useRef } from "react";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { UploadFile, CheckCircle, WarningAmber } from "@mui/icons-material";
import api from "../../api";

export default function LabUploadButton({ jobId, onUploadSuccess }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Hata/Uyarı Modalı State'leri
  const [openModal, setOpenModal] = useState(false);
  const [warnings, setWarnings] = useState([]);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setSuccess(false);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("jobId", jobId);

    try {
      const res = await api.post(`/admin/upload-lab/${jobId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setSuccess(true);
        if (onUploadSuccess) onUploadSuccess();

        // Eğer backend'den uyarılar (warnings) döndüyse Modalı aç
        if (res.data.warnings && res.data.warnings.length > 0) {
          setWarnings(res.data.warnings);
          setOpenModal(true);
        } else {
          alert(`Başarılı! ${res.data.message}`);
        }
      }
    } catch (err) {
      console.error("Lab yükleme hatası:", err);
      alert(
        "Yükleme başarısız: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  return (
    <>
      {/* Gizli Dosya Inputu */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".xlsx, .xls, .csv"
        onChange={handleFileChange}
      />

      <Tooltip title="Lab Verisi Yükle (Excel)">
        <IconButton
          color={success ? "success" : "default"}
          onClick={handleButtonClick}
          disabled={loading}
          sx={{
            mr: 1,
            color: success ? "green" : "#2e7d32",
            bgcolor: loading ? "transparent" : "rgba(46, 125, 50, 0.1)",
            "&:hover": { bgcolor: "rgba(46, 125, 50, 0.2)" },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : success ? (
            <CheckCircle />
          ) : (
            <UploadFile />
          )}
        </IconButton>
      </Tooltip>

      {/* HATA/UYARI MODALI */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "warning.main",
            fontWeight: "bold",
          }}
        >
          <WarningAmber /> Yükleme Başarılı Ancak {warnings.length} Uyuşmazlık
          Tespit Edildi!
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Laboratuvar verileri sisteme aktarıldı, ancak hesaplama motorumuz
            (Toprak Bünye Üçgeni) aşağıdaki satırlarda laboratuvar sonuçlarıyla
            uyuşmazlık veya veri hatası tespit etti. (Sistem hesaplanan doğru
            değeri kaydetti).
          </Typography>
          <List dense>
            {warnings.map((warn, index) => (
              <ListItem
                key={index}
                sx={{
                  bgcolor: "rgba(237, 108, 2, 0.05)",
                  mb: 1,
                  borderRadius: 1,
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 48, display: "flex", alignItems: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mr: 1, color: "warning.main" }}
                  >
                    {index + 1}.
                  </Typography>
                  <WarningAmber fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary={warn} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenModal(false)}
            variant="contained"
            color="primary"
          >
            Anladım, Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
